import {
  ArrowRight,
  BookOpen,
  Globe,
  Lock,
  Server,
  Shield,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router";
import { getTranslationFn } from "../../i18n";
import type { Locale } from "../../lib/locale";
import * as Paths from "../../paths";
import { MarketingCta } from "./marketing-cta";
import { MarketingLayout } from "./marketing-layout";

interface SecurityPageProps {
  locale: Locale;
}

export function SecurityPage({ locale }: SecurityPageProps) {
  const t = getTranslationFn(locale);

  const securityFeatures = [
    {
      icon: ShieldCheck,
      title: t("Landing.securitySummary.zeroTraining.title"),
      body: t("Landing.securitySummary.zeroTraining.description"),
    },
    {
      icon: Shield,
      title: t("Landing.securitySummary.isolation.title"),
      body: t("Landing.securitySummary.isolation.description"),
    },
    {
      icon: Globe,
      title: t("Landing.securitySummary.hosting.title"),
      body: t("Landing.securitySummary.hosting.description"),
    },
    {
      icon: Lock,
      title: t("Landing.securitySummary.encryption.title"),
      body: t("Landing.securitySummary.encryption.description"),
    },
    {
      icon: Server,
      title: t("Landing.securitySummary.privateCloud.title"),
      body: t("Landing.securitySummary.privateCloud.description"),
    },
  ];

  return (
    <MarketingLayout locale={locale}>
      {/* Hero */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <BookOpen className="w-10 h-10 text-brand-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
              {t("Security.hero.title")}
            </h1>
            <p className="mt-6 text-xl text-slate-500 leading-relaxed font-light max-w-2xl mx-auto">
              {t("Security.hero.description")}
            </p>
          </div>
        </div>
      </section>

      {/* Security features grid */}
      <section className="pb-16 md:pb-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {securityFeatures.map((item) => (
              <div
                key={item.title}
                className="p-6 rounded-2xl border border-slate-100 hover:border-brand-200 transition-colors h-full"
              >
                <item.icon className="w-6 h-6 text-brand-600 mb-4" />
                <h3 className="text-base font-semibold text-slate-900 tracking-tight mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed font-light">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Link to full docs */}
      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-4 text-center">
          <Link
            to={Paths.docsPage("bezpieczenstwo-i-prywatnosc")}
            className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700 font-medium transition-colors"
          >
            {t("Security.docsLink")}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <MarketingCta locale={locale} />
    </MarketingLayout>
  );
}
